import type { Playlist, PlaylistItem } from '../../types/playlist';
import { CONTENT_TYPE } from '../constants';

type RequiredProperties<T, P extends keyof T> = T & Required<Pick<T, P>>;

type DeprecatedPlaylistItem = {
  seriesPlayListId?: string;
  seriesPlaylistId?: string;
};

export const isPlaylist = (item: unknown): item is Playlist => !!item && typeof item === 'object' && 'feedid' in item;
export const isPlaylistItem = (item: unknown): item is PlaylistItem => !!item && typeof item === 'object' && 'mediaid' in item;

// For the deprecated series flow we store seriesId in custom params
export const getSeriesPlaylistIdFromCustomParams = (item: (PlaylistItem & DeprecatedPlaylistItem) | undefined) =>
  item ? item.seriesPlayListId || item.seriesPlaylistId || item.seriesId : undefined;

// For the deprecated flow we store seriesId in the media custom params
export const isLegacySeriesFlow = (item: PlaylistItem) => {
  return typeof getSeriesPlaylistIdFromCustomParams(item) !== 'undefined';
};

// For the new series flow we use contentType custom param to define media item to be series
// In this case media item and series item have the same id
export const isSeriesContentType = (item: PlaylistItem) => item.contentType?.toLowerCase() === CONTENT_TYPE.series;

export const isSeries = (item: PlaylistItem) => isLegacySeriesFlow(item) || isSeriesContentType(item);

export const isEpisode = (item: PlaylistItem) => {
  return typeof item?.episodeNumber !== 'undefined' || item?.contentType?.toLowerCase() === CONTENT_TYPE.episode;
};

export const getLegacySeriesPlaylistIdFromEpisodeTags = (item: PlaylistItem | undefined) => {
  if (!item || !isEpisode(item)) {
    return;
  }

  const tags = item.tags ? item.tags.split(',') : [];
  const seriesIdTag = tags.find(function (tag) {
    return /seriesid_([\w\d]+)/i.test(tag);
  });

  if (seriesIdTag) {
    return seriesIdTag.split('_')[1];
  }
};

export const isLiveChannel = (item: PlaylistItem): item is RequiredProperties<PlaylistItem, 'contentType' | 'liveChannelsId'> =>
  item.contentType?.toLowerCase() === CONTENT_TYPE.liveChannel && !!item.liveChannelsId;

const NON_VIDEO_CONTENT_TYPES = new Set([
  CONTENT_TYPE.series,
  CONTENT_TYPE.episode,
  CONTENT_TYPE.live,
  CONTENT_TYPE.liveChannel,
  CONTENT_TYPE.page,
  CONTENT_TYPE.hub,
  'reels',
  'reel',
  'liveevent',
]);

/** UGC / catalog Video items (`video` / `videos`). Distinct from Movies (empty or `movie` contentType). */
export const isVideoContentType = (item: PlaylistItem | undefined | null) => {
  const contentType = item?.contentType?.toLowerCase();
  return contentType === 'video' || contentType === 'videos';
};

export const isReelContentType = (item: PlaylistItem | undefined | null) => {
  const contentType = item?.contentType?.toLowerCase();
  return contentType === 'reels' || contentType === 'reel';
};

/** Videos skip the details page and open playback directly on select. */
export const shouldPlayOnSelect = (item: PlaylistItem) => isVideoContentType(item);

/** True for VOD/movie items (including default/empty contentType). False for series, reels, live, etc. */
export const isVideo = (item: PlaylistItem) => {
  if (isSeries(item) || isEpisode(item) || isLiveChannel(item)) {
    return false;
  }

  const contentType = item.contentType?.toLowerCase();
  if (!contentType || contentType === 'video' || contentType === 'videos') {
    return true;
  }

  return !NON_VIDEO_CONTENT_TYPES.has(contentType);
};
